import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { AlertTriangle, Check, CheckCircle2, ChevronsUpDown, Loader2, MinusCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { useToast } from "@/shared/hooks/use-toast";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import {
  OrganizationService,
  type AiModelOutcome,
} from "@/features/Organization/services/organizationService";
import { IntegrationBaseService } from "@/features/Integrations/services/integrationsBaseService";
import { ToolSelect, type ToolOption } from "@/shared/components/ui/tool-select";
import { getIntegrationDescription } from "@/shared/utils/i18nHelper";

/**
 * Campo de modelo: lista o que o cadastro declara.
 *
 * Os dois comportamentos são necessários. A lista vem de
 * `metadata.models` e é o que evita erro de digitação — foi de uma lista
 * mantida à mão, divergente do cadastro, que veio o `:free` que ninguém reconhecia.
 * A digitação livre é o que mantém o Azure funcionando: ali o valor é o nome do
 * DEPLOYMENT no recurso do cliente, escolhido por quem instalou, e não precisa
 * coincidir com o nome do modelo. Integração sem catálogo (o `grok`, hoje) também
 * ficaria travada se a lista fosse a única entrada (AB#85434).
 */
/**
 * Escolha de modelo, fechada no que o cadastro declara.
 *
 * Nao aceita texto livre: modelo digitado a mao chega ao gateway sem rota, sem
 * api-version, sem limites de contexto e sem preco, e cada uma dessas ausencias vira um
 * default silencioso. Onde a instalacao nomeia o proprio deployment, esse nome e
 * declarado no formulario da integracao — a escolha aqui continua sendo entre coisas que
 * o cadastro descreve (AB#85434).
 */
function ModelCombobox({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  emptyLabel,
  searchLabel,
  status,
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  emptyLabel: string;
  searchLabel: string;
  status?: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled || options.length === 0}
        >
          {value ? (
            <span className="truncate font-mono text-xs">{value}</span>
          ) : (
            <span className="text-muted-foreground">
              {options.length === 0 ? emptyLabel : placeholder}
            </span>
          )}
          <span className="ml-2 flex shrink-0 items-center gap-2">
            {status}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchLabel} />
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option}
                  value={option}
                  onSelect={() => {
                    onChange(option);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${option === value ? "opacity-100" : "opacity-0"}`}
                  />
                  <span className="font-mono text-xs">{option}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Resultado da validacao de um campo — ver o estado `checks` (AB#85434). */
type FieldCheck =
  | { state: "idle" }
  | { state: "checking" }
  | { state: "done"; outcome: AiModelOutcome }
  /** A chamada nao aconteceu (rede, permissao): nada se pode afirmar do modelo. */
  | { state: "unreachable" }
  /** O relatorio veio, mas sem poder testar — sem gateway, sem integracao. */
  | { state: "unavailable"; reason: string };

const aprovado = (check: FieldCheck) => check.state === "done" && check.outcome.status === "ok";

export const ModelsSettings = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { selectedOrganization } = useSuperAdminOrg();
  const queryClient = useQueryClient();

  const activeOrgId = selectedOrganization?.id;
  const effectiveOrgId = activeOrgId ?? user?.organizationId;

  /**
   * Uma integração por propósito (AB#85432).
   *
   * Antes era uma só, e a organização que conversava por um provedor era obrigada a
   * indexar por ele — o que travava justamente quem usa um provedor que não serve
   * embedding. São duas escolhas independentes, e cada uma carrega o próprio catálogo.
   */
  const [convIntegrationId, setConvIntegrationId] = useState<string | null>(null);
  const [initialConvIntegrationId, setInitialConvIntegrationId] = useState<string | null>(null);
  const [embIntegrationId, setEmbIntegrationId] = useState<string | null>(null);
  const [initialEmbIntegrationId, setInitialEmbIntegrationId] = useState<string | null>(null);
  /**
   * Modelos que o cadastro declara, separados pelo que cada um serve.
   *
   * Fonte única: `metadata.models`, agrupados pelo `api` que cada entrada
   * declara — o endpoint em que o modelo responde. Sem esse agrupamento o campo
   * conversacional sugeria `text-embedding-ada-002`: a lista era "todas as chaves"
   * (AB#85434).
   */
  const [conversationalModels, setConversationalModels] = useState<string[]>([]);
  const [embeddingModels, setEmbeddingModels] = useState<string[]>([]);
  const [completion, setCompletion] = useState<string>("");
  const [initialCompletion, setInitialCompletion] = useState<string>("");
  const [embedding, setEmbedding] = useState<string>("");
  const [initialEmbedding, setInitialEmbedding] = useState<string>("");
  /**
   * Resultado da validacao de UM campo.
   *
   * `unreachable` e `unavailable` existem separados de `done` porque nao dizem nada sobre
   * o modelo: a chamada nao aconteceu, ou o relatorio veio sem gateway configurado.
   * Trata-los como reprovacao acusaria o cadastro por uma falha de rede.
   */
  const [checks, setChecks] = useState<{ conversational: FieldCheck; embedding: FieldCheck }>({
    conversational: { state: "idle" },
    embedding: { state: "idle" },
  });

  const { data: escolha, isLoading: isLoadingOrg } = useQuery({
    queryKey: ['organization-ai-models', effectiveOrgId],
    queryFn: async () => {
      if (!effectiveOrgId) return null;
      return await OrganizationService.getAiModels(effectiveOrgId);
    },
    enabled: !!effectiveOrgId,
  });

  useEffect(() => {
    if (!escolha) return;
    const conv = escolha.conversational;
    const emb = escolha.embedding;
    setConvIntegrationId(conv?.integration_base_id ?? null);
    setInitialConvIntegrationId(conv?.integration_base_id ?? null);
    setCompletion(conv?.model ?? "");
    setInitialCompletion(conv?.model ?? "");
    // Sem escolha de embedding, o campo abre na integração do conversacional: é o caso
    // comum, e deixá-lo vazio esconderia que há um catálogo a escolher.
    setEmbIntegrationId(emb?.integration_base_id ?? conv?.integration_base_id ?? null);
    setInitialEmbIntegrationId(emb?.integration_base_id ?? null);
    setEmbedding(emb?.model ?? "");
    setInitialEmbedding(emb?.model ?? "");
  }, [escolha]);

  const { data: providers, isLoading: isLoadingProviders } = useQuery<ToolOption[]>({
    queryKey: ['integration-base-models', effectiveOrgId, i18n.language],
    queryFn: async (): Promise<ToolOption[]> => {
      if (!effectiveOrgId) return [];
      const integrationsBases = await IntegrationBaseService.getAll({
        classification: 'llm',
        status: 'active'
      });
      return integrationsBases.map((integration): ToolOption => ({
        id: integration.id,
        name: integration.label,
        description: getIntegrationDescription(integration, i18n.language) || '',
        logo_url: integration.logo_url || integration.template?.logo_url || null
      }));
    },
    enabled: !!effectiveOrgId,
  });

  /**
   * Catalogo declarado por UMA integracao, ja separado pelo que cada modelo serve.
   *
   * Um eixo so: `api: embedding` responde em embeddings, `chat` e `responses` sao conversa.
   * Ausencia vale `chat`, como o gateway sempre leu — e o que a maioria das entradas e, e
   * mantem funcionando cadastro que ainda nao foi declarado.
   */
  const carregarCatalogo = async (integrationId: string) => {
    const integration = await IntegrationBaseService.getById(integrationId);
    const capabilities = (integration?.metadata as Record<string, unknown> | undefined)
      ?.models;
    const declared =
      capabilities && typeof capabilities === "object" && !Array.isArray(capabilities)
        ? Object.entries(capabilities as Record<string, unknown>)
        : [];
    const isEmbedding = (entry: unknown) =>
      entry !== null &&
      typeof entry === "object" &&
      !Array.isArray(entry) &&
      (entry as Record<string, unknown>).api === "embedding";
    return {
      conversacionais: declared.filter(([, e]) => !isEmbedding(e)).map(([k]) => k).sort(),
      embeddings: declared.filter(([, e]) => isEmbedding(e)).map(([k]) => k).sort(),
    };
  };

  // Um catalogo por proposito, cada um da SUA integracao. A guarda de cancelamento existe
  // porque trocar de provedor dispara um fetch novo sem cancelar o anterior, e a resposta
  // que chega por ultimo vence — a tela mostraria os modelos de uma integracao com outra
  // selecionada.
  useEffect(() => {
    let cancelado = false;
    if (!convIntegrationId) {
      setConversationalModels([]);
      return;
    }
    carregarCatalogo(convIntegrationId)
      .then(({ conversacionais }) => {
        if (cancelado) return;
        setConversationalModels(conversacionais);
        // Modelo que a nova integração não declara não pode continuar selecionado: ele
        // seria chamado com a credencial dela e falharia. Sem fallback para `settings` —
        // a integração descreve o provedor, não a escolha da organização.
        setCompletion((atual) => (atual && conversacionais.includes(atual) ? atual : ""));
      })
      .catch((error) => console.error("[ModelsSettings] catálogo conversacional:", error));
    return () => {
      cancelado = true;
    };
  }, [convIntegrationId]);

  useEffect(() => {
    let cancelado = false;
    if (!embIntegrationId) {
      setEmbeddingModels([]);
      return;
    }
    carregarCatalogo(embIntegrationId)
      .then(({ embeddings }) => {
        if (cancelado) return;
        setEmbeddingModels(embeddings);
        setEmbedding((atual) => (atual && embeddings.includes(atual) ? atual : ""));
      })
      .catch((error) => console.error("[ModelsSettings] catálogo de embedding:", error));
    return () => {
      cancelado = true;
    };
  }, [embIntegrationId]);

  /**
   * Testa os modelos que acabaram de ser gravados.
   *
   * Fora do `useMutation` do save de proposito: falhar aqui nao pode marcar o
   * save como falho. O relatorio e informacao, e um modelo reprovado e RESULTADO
   * do teste, nao erro da tela.
   */
  /**
   * Valida UM proposito, com a escolha que esta na TELA (AB#85434).
   *
   * Antes isto rodava depois do save, e o relatorio chegava com a configuracao errada ja
   * gravada — a organizacao ficava sem LLM ate alguem reabrir a tela. Validando antes, o
   * Save so libera com os dois campos aprovados.
   *
   * Manda em branco o modelo do OUTRO proposito: em branco significa "nao teste este", e a
   * api devolve `skipped` para ele. Assim trocar um campo nao gasta uma chamada ao provedor
   * com o outro, que nao mudou.
   */
  const checarModelo = useCallback(
    async (
      purpose: "conversational" | "embedding",
      integrationId: string | null,
      model: string,
      cancelado: () => boolean
    ) => {
      if (!effectiveOrgId || !integrationId || !model) {
        setChecks((atual) => ({ ...atual, [purpose]: { state: "idle" } }));
        return;
      }

      setChecks((atual) => ({ ...atual, [purpose]: { state: "checking" } }));
      const aplicar = (check: FieldCheck) => {
        if (cancelado()) return;
        setChecks((atual) => ({ ...atual, [purpose]: check }));
      };

      try {
        const report = await OrganizationService.testAiModels(effectiveOrgId, {
          purpose,
          integration_base_id: integrationId,
          model,
        });
        aplicar(
          report.status === "ok"
            ? { state: "done", outcome: report[purpose] }
            : { state: "unavailable", reason: report.status }
        );
      } catch {
        aplicar({ state: "unreachable" });
      }
    },
    [effectiveOrgId]
  );

  // Um efeito por campo: trocar o conversacional nao revalida o embedding. Trocar de
  // PROVEDOR revalida os dois, e deve mesmo — os dois modelos passam a ser de outra
  // integracao, com outra credencial e outro endpoint.
  //
  // A guarda de cancelamento existe porque a resposta mais lenta de uma escolha anterior
  // sobrescreveria a atual, aprovando um modelo que ja nao esta selecionado.
  useEffect(() => {
    let cancelado = false;
    void checarModelo("conversational", convIntegrationId, completion, () => cancelado);
    return () => {
      cancelado = true;
    };
  }, [checarModelo, convIntegrationId, completion]);

  useEffect(() => {
    let cancelado = false;
    void checarModelo("embedding", embIntegrationId, embedding, () => cancelado);
    return () => {
      cancelado = true;
    };
  }, [checarModelo, embIntegrationId, embedding]);

  const save = useMutation({
    mutationFn: async () => {
      if (!effectiveOrgId) throw new Error(t('common.errors.organizationNotFound'));

      if (!convIntegrationId) throw new Error(t('common.errors.organizationNotFound'));

      // Uma chamada só. Antes eram duas — organização e integração —, e a escolha
      // ficava pela metade quando a segunda falhava. O backend grava as duas linhas e as
      // chaves de compatibilidade na mesma transação (AB#85434).
      await OrganizationService.updateAiModels(effectiveOrgId, {
        conversational_integration_base_id: convIntegrationId,
        conversational_model: completion,
        embedding_integration_base_id: embIntegrationId,
        embedding_model: embedding,
      });
    },
    onSuccess: () => {
      setInitialConvIntegrationId(convIntegrationId);
      setInitialEmbIntegrationId(embIntegrationId);
      setInitialCompletion(completion);
      setInitialEmbedding(embedding);
      toast({
        title: t("settings.models.updateSuccess"),
        description: t("settings.models.updateSuccessDescription"),
      });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['organization-ai-models'] });
    },
    onError: (error) => {
      console.error('[ModelsSettings] Save error:', error);
      toast({
        variant: "destructive",
        title: t("common.error"),
        // A mensagem do backend, quando ele diz POR QUE recusou — uma escolha de modelo
        // que a base nao suporta, por exemplo. O texto generico deixava o usuario sem
        // saber que o problema era o modelo que ele acabou de escolher (AB#85518).
        description: resolveApiErrorMessage(error, t),
      });
    }
  });

  /**
   * Valor gravado que o cadastro atual nao declara — sobra de quando a tela aceitava texto
   * livre, ou de uma troca de provedor. A escolha ja nao permite chegar nesse estado, mas
   * ele existe em dado antigo, e some da lista sem aviso se a tela nao disser nada.
   */
  const OffCatalogWarning = ({ value, options }: { value: string; options: string[] }) => {
    if (!value || options.length === 0 || options.includes(value)) return null;
    return (
      <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-500">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>{t("settings.models.offCatalog")}</span>
      </p>
    );
  };

  /**
   * O veredito dentro do proprio campo: e ali que a pessoa olha ao escolher.
   */
  const StatusIcon = ({ check }: { check: FieldCheck }) => {
    if (check.state === "checking")
      return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    if (check.state === "idle") return null;
    if (aprovado(check)) return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
    if (check.state === "done" && check.outcome.status === "skipped")
      return <MinusCircle className="h-4 w-4 text-muted-foreground" />;
    return <AlertTriangle className="h-4 w-4 text-destructive" />;
  };

  /**
   * A mensagem abaixo do campo, so quando ha o que dizer.
   *
   * Ela nomeia a rota usada E a procedencia dela: quem le e o admin da organizacao, quem
   * conserta o cadastro e o superadmin, e sem as duas coisas o admin nao tem o que pedir.
   */
  const CheckMessage = ({ check }: { check: FieldCheck }) => {
    if (check.state === "idle" || check.state === "checking" || aprovado(check)) return null;

    if (check.state === "unreachable")
      return (
        <p className="text-xs text-muted-foreground">{t("settings.models.test.unreachable")}</p>
      );

    if (check.state === "unavailable")
      return (
        <p className="text-xs text-muted-foreground">
          {t(`settings.models.test.unavailable.${check.reason}`)}
        </p>
      );

    const outcome = check.outcome;
    if (outcome.status === "skipped")
      return (
        <p className="text-xs text-muted-foreground">{t("settings.models.test.skipped")}</p>
      );
    if (outcome.status === "ok") return null;

    // `provider_unavailable` nao acusa o cadastro: o provedor nao respondeu, e mandar o
    // admin procurar o superadmin por isso desperdicaria o tempo dos dois.
    const blamesRegistry = outcome.reason !== "provider_unavailable";

    return (
      <div className="flex items-start gap-1.5 text-xs text-destructive">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          {t("settings.models.test.failed", { route: outcome.route })}
          <span className="text-muted-foreground">
            {" "}
            — {t(`settings.models.test.routeFrom.${outcome.route_source}`)}
          </span>
          <span className="block text-muted-foreground">
            {t(`settings.models.test.reason.${outcome.reason}`)}
            {blamesRegistry ? ` ${t("settings.models.test.askSuperadmin")}` : ""}
          </span>
          {/* A explicacao que o provedor (ou o gateway) devolveu. Sem ela a tela descreve a
              CATEGORIA da falha e esconde a frase que resolve a duvida — foi um
              `embed is not supported by openrouter` virando "tente novamente" que motivou
              o AB#85434. Ja vem saneada da api. */}
          {outcome.detail ? (
            <span className="mt-1 block break-words font-mono text-[11px] text-muted-foreground">
              {outcome.detail}
            </span>
          ) : null}
        </span>
      </div>
    );
  };

  const hasChanges =
    convIntegrationId !== initialConvIntegrationId ||
    embIntegrationId !== initialEmbIntegrationId ||
    completion !== initialCompletion ||
    embedding !== initialEmbedding;

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t("settings.models.title")}</CardTitle>
        <CardDescription>{t("settings.models.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoadingOrg ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
            className="space-y-4"
          >
            {/* Um provedor POR PROPÓSITO (AB#85432): quem conversa por um provedor que não
                serve embedding precisa indexar por outro, e antes a tela obrigava os dois
                a serem o mesmo. */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t("settings.organization.conversationalProvider")}
              </label>
              <ToolSelect
                value={convIntegrationId}
                onValueChange={setConvIntegrationId}
                options={providers || []}
                placeholder={t("settings.organization.selectModel")}
                disabled={isLoadingProviders}
                loading={isLoadingProviders}
                allowEmpty={true}
                emptyLabel={t("settings.organization.noModel")}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t("settings.organization.defaultModelName")}
              </label>
              <ModelCombobox
                value={completion}
                onChange={setCompletion}
                options={conversationalModels}
                placeholder={t("settings.organization.defaultModelNamePlaceholder")}
                disabled={!convIntegrationId}
                emptyLabel={t("settings.models.noDeclaredModels")}
                searchLabel={t("settings.models.searchModels")}
                status={<StatusIcon check={checks.conversational} />}
              />
              <p className="text-xs text-muted-foreground">
                {t("settings.organization.defaultModelNameHelp")}
              </p>
              <OffCatalogWarning value={completion} options={conversationalModels} />
              <CheckMessage check={checks.conversational} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t("settings.organization.embeddingProvider")}
              </label>
              <ToolSelect
                value={embIntegrationId}
                onValueChange={setEmbIntegrationId}
                options={providers || []}
                placeholder={t("settings.organization.selectModel")}
                disabled={isLoadingProviders}
                loading={isLoadingProviders}
                allowEmpty={true}
                emptyLabel={t("settings.organization.noModel")}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t("settings.organization.defaultEmbeddingModel")}
              </label>
              <ModelCombobox
                value={embedding}
                onChange={setEmbedding}
                options={embeddingModels}
                placeholder={t("settings.organization.defaultEmbeddingModelPlaceholder")}
                disabled={!embIntegrationId}
                emptyLabel={t("settings.models.noDeclaredModels")}
                searchLabel={t("settings.models.searchModels")}
                status={<StatusIcon check={checks.embedding} />}
              />
              <p className="text-xs text-muted-foreground">
                {t("settings.organization.defaultEmbeddingModelHelp")}
              </p>
              <OffCatalogWarning value={embedding} options={embeddingModels} />
              <CheckMessage check={checks.embedding} />
            </div>
            {/* O Save espera os dois vereditos. Gravar um modelo que nao responde deixa a
                organizacao sem LLM ate alguem reabrir esta tela — e o erro so aparece
                depois, dentro do Playground ou de um agente (AB#85434). */}
            <Button
              type="submit"
              disabled={
                save.isPending ||
                !hasChanges ||
                !aprovado(checks.conversational) ||
                !aprovado(checks.embedding)
              }
              className="w-full sm:w-auto"
            >
              {save.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("common.actions.saving")}
                </>
              ) : (
                t("common.actions.save")
              )}
            </Button>

          </form>
        )}
      </CardContent>
    </Card>
  );
};
